import React, { useState, useEffect, useRef } from 'react'
import * as api from '../../services/api.js'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import AppSelect from '../../components/AppSelect.jsx'

// ── Constants ─────────────────────────────────────────────────────────────────

const BEAT_TYPES = [
  { id: 'scene',         label: 'Scene',         color: 'var(--stone)' },
  { id: 'chapter_break', label: 'Chapter break', color: 'var(--bark)' },
  { id: 'turning_point', label: 'Turning point', color: 'var(--gold)' },
  { id: 'midpoint',      label: 'Midpoint',      color: '#60a5fa' },
  { id: 'crisis',        label: 'Crisis',        color: '#f87171' },
  { id: 'climax',        label: 'Climax',        color: '#a78bfa' },
  { id: 'resolution',    label: 'Resolution',    color: 'var(--sage)' },
]

const STATUS_DOTS = {
  planned: 'var(--stone)',
  drafted: '#60a5fa',
  revised: 'var(--sage)',
}

const ACTS = [
  { num: 1, label: 'Act I — Setup',        hint: 'Opening, inciting incident, first turning point' },
  { num: 2, label: 'Act II — Confrontation', hint: 'Rising action, midpoint, crisis, darkest moment' },
  { num: 3, label: 'Act III — Resolution', hint: 'Climax, falling action, resolution' },
]

const ACT_COLORS = { 1: '#60a5fa', 2: 'var(--gold)', 3: 'var(--sage)' }

const BEAT_TYPE_OPTIONS = BEAT_TYPES.map(t => ({ value: t.id, label: t.label }))
const ACT_OPTIONS       = [{ value: '1', label: 'Act I' }, { value: '2', label: 'Act II' }, { value: '3', label: 'Act III' }]
const STATUS_OPTIONS    = [{ value: 'planned', label: 'Planned' }, { value: 'drafted', label: 'Drafted' }, { value: 'revised', label: 'Revised' }]

// ── BeatPanel ─────────────────────────────────────────────────────────────────

function BeatPanel({ beat, prefilledAct, pid, chapters, onClose, onSaved, onDeleted }) {
  const isNew = !beat
  const [form, setForm] = useState({
    title:       beat?.title       || '',
    beat_type:   beat?.beat_type   || 'scene',
    act:         beat?.act         || prefilledAct || 1,
    status:      beat?.status      || 'planned',
    description: beat?.description || '',
    chapter_id:  beat?.chapter_id  || '',
  })
  const [open, setOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const titleRef = useRef(null)

  useEffect(() => {
    setTimeout(() => setOpen(true), 10)
    setTimeout(() => titleRef.current?.focus(), 60)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') handleClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  function handleClose() {
    setOpen(false)
    setTimeout(onClose, 260)
  }

  async function handleSave() {
    if (!form.title.trim()) return
    const payload = {
      ...form,
      title:      form.title.trim(),
      project_id: pid,
      position:   beat?.position ?? 0,
    }
    if (isNew) {
      const res = await api.savePlotBeat(payload)
      onSaved({ ...payload, id: res?.id || Date.now() })
    } else {
      await api.updatePlotBeat(beat.id, payload, pid)
      onSaved({ ...beat, ...payload })
    }
    handleClose()
  }

  async function handleDelete() {
    setShowDeleteConfirm(true)
  }

  async function confirmDelete() {
    setShowDeleteConfirm(false)
    await api.deletePlotBeat(beat.id, pid)
    onDeleted(beat.id)
    handleClose()
  }

  const fs = {
    width: '100%', boxSizing: 'border-box',
    padding: '6px 9px', fontSize: '0.85rem',
    border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8,
    background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'var(--font-sans)',
  }

  return (
    <>
      <div
        className="b-panel-backdrop"
        onClick={handleClose}
        style={{ opacity: open ? 1 : 0, transition: 'opacity 0.25s' }}
      />
      <div className={`b-idea-panel${open ? ' open' : ''}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.1rem' }}>
          <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--stone)' }}>
            {isNew ? 'New beat' : 'Edit beat'}
          </span>
          <button className="panel-close-btn" onClick={handleClose}>✕</button>
        </div>

        <div style={{ marginBottom: '0.7rem' }}>
          <div className="b-panel-label">Title</div>
          <input ref={titleRef} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Beat title" style={fs} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.7rem' }}>
          <div>
            <div className="b-panel-label">Type</div>
            <AppSelect
              value={form.beat_type}
              onChange={v => setForm(f => ({ ...f, beat_type: v }))}
              options={BEAT_TYPE_OPTIONS}
            />
          </div>
          <div>
            <div className="b-panel-label">Act</div>
            <AppSelect
              value={String(form.act)}
              onChange={v => setForm(f => ({ ...f, act: Number(v) }))}
              options={ACT_OPTIONS}
            />
          </div>
        </div>

        <div style={{ marginBottom: '0.7rem' }}>
          <div className="b-panel-label">Status</div>
          <AppSelect
            value={form.status}
            onChange={v => setForm(f => ({ ...f, status: v }))}
            options={STATUS_OPTIONS}
          />
        </div>

        <div style={{ marginBottom: '0.7rem' }}>
          <div className="b-panel-label">Description</div>
          <textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="What happens in this beat? What changes?"
            rows={3}
            style={{ ...fs, resize: 'vertical', fontFamily: 'var(--font-serif)', lineHeight: 1.65 }}
          />
        </div>

        {chapters.length > 0 && (
          <div style={{ marginBottom: '0.7rem' }}>
            <div className="b-panel-label">Chapter link</div>
            <AppSelect
              value={form.chapter_id}
              onChange={v => setForm(f => ({ ...f, chapter_id: v }))}
              options={[{ value: '', label: 'None' }, ...chapters.map(c => ({ value: String(c.id), label: c.title || 'Untitled chapter' }))]}
            />
          </div>
        )}

        <button
          className="b-panel-save-btn"
          onClick={handleSave}
          disabled={!form.title.trim()}
          style={{ opacity: form.title.trim() ? 1 : 0.45 }}
        >
          {isNew ? 'Add beat' : 'Save changes'}
        </button>

        {!isNew && (
          <div style={{ borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: '0.9rem', marginTop: '1rem' }}>
            <button
              onClick={handleDelete}
              style={{ fontSize: '0.72rem', color: '#DC2626', background: 'transparent', border: '1px solid #DC2626', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
            >
              Remove beat
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Remove this beat?"
        message="This cannot be undone."
        confirmLabel="Remove"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  )
}

// ── BeatCard ──────────────────────────────────────────────────────────────────

function BeatCard({ beat, beats, onEdit, onMoveUp, onMoveDown, onMoveToAct }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const actBeats = beats.filter(b => b.act === beat.act).sort((a, b) => a.position - b.position)
  const idx      = actBeats.findIndex(b => b.id === beat.id)
  const typeInfo = BEAT_TYPES.find(t => t.id === beat.beat_type)

  return (
    <div
      className="beat-card"
      style={{ borderLeftColor: typeInfo?.color || 'var(--stone)' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span className="beat-type-badge">{typeInfo?.label || beat.beat_type}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span className="beat-status-dot" style={{ background: STATUS_DOTS[beat.status] || 'var(--stone)' }} title={beat.status} />
          <button className="b-arrow-btn" onClick={onMoveUp}  disabled={idx === 0} style={{ opacity: idx === 0 ? 0.35 : 1 }}>↑</button>
          <button className="b-arrow-btn" onClick={onMoveDown} disabled={idx === actBeats.length - 1} style={{ opacity: idx === actBeats.length - 1 ? 0.35 : 1 }}>↓</button>
          <button className="b-arrow-btn" onClick={() => onEdit(beat)}>✎</button>
        </div>
      </div>

      <div className="beat-card-title">{beat.title}</div>

      {beat.description && (
        <div className="beat-card-desc">{beat.description}</div>
      )}

      {beat.chapter_id && (
        <div style={{ fontSize: '0.65rem', color: 'var(--stone)', marginTop: 4 }}>Ch: {beat.chapter_id}</div>
      )}

      {/* Move to act */}
      <div style={{ marginTop: 6, position: 'relative' }}>
        <button
          className="b-move-act-btn"
          onClick={() => setMenuOpen(o => !o)}
        >
          Move to act ▾
        </button>
        {menuOpen && (
          <div className="b-move-act-menu">
            {ACTS.filter(a => a.num !== beat.act).map(a => (
              <div key={a.num} className="b-move-act-option" onClick={() => { onMoveToAct(beat.id, a.num); setMenuOpen(false) }}>
                {a.label.split(' — ')[0]}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── PlotDot tooltip ───────────────────────────────────────────────────────────

function PlotDot({ beat, onClick }) {
  const [tip, setTip] = useState(false)
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        className="plot-dot"
        style={{ background: ACT_COLORS[beat.act] || 'var(--stone)', cursor: 'pointer' }}
        onMouseEnter={() => setTip(true)}
        onMouseLeave={() => setTip(false)}
        onClick={() => onClick(beat.id)}
        title={beat.title}
      />
      {tip && (
        <div className="plot-dot-tip">
          {beat.title}
        </div>
      )}
    </div>
  )
}

// ── PlotPlanner main ──────────────────────────────────────────────────────────

export default function PlotPlanner({ pid }) {
  const [beats,    setBeats]    = useState([])
  const [chapters, setChapters] = useState([])
  const [panelOpen, setPanelOpen] = useState(false)
  const [editBeat,  setEditBeat]  = useState(null)
  const [prefilledAct, setPrefilledAct] = useState(1)
  const beatRefs = useRef({})

  useEffect(() => {
    api.getPlotBeats(pid).then(d => setBeats(d || []))
    api.getChapters(pid).then(d => setChapters(d || []))
  }, [pid])

  function openAdd(act) {
    setEditBeat(null)
    setPrefilledAct(act)
    setPanelOpen(true)
  }

  function openEdit(beat) {
    setEditBeat(beat)
    setPanelOpen(true)
  }

  function handleSaved(saved) {
    setBeats(prev => {
      const exists = prev.find(b => b.id === saved.id)
      if (exists) return prev.map(b => b.id === saved.id ? saved : b)
      // New beat: assign position = max position + 1 in that act
      const actBeats = prev.filter(b => b.act === saved.act)
      const pos = actBeats.length > 0 ? Math.max(...actBeats.map(b => b.position)) + 1 : 0
      return [...prev, { ...saved, position: pos }]
    })
  }

  function handleDeleted(id) {
    setBeats(prev => prev.filter(b => b.id !== id))
  }

  function moveWithinAct(id, direction) {
    setBeats(prev => {
      const beat = prev.find(b => b.id === id)
      if (!beat) return prev
      const actBeats = [...prev.filter(b => b.act === beat.act)].sort((a, b) => a.position - b.position)
      const idx = actBeats.findIndex(b => b.id === id)
      const swapIdx = idx + direction
      if (swapIdx < 0 || swapIdx >= actBeats.length) return prev

      const a = actBeats[idx]
      const b = actBeats[swapIdx]
      const posA = a.position
      const posB = b.position
      api.updatePlotBeat(a.id, { position: posB }, pid)
      api.updatePlotBeat(b.id, { position: posA }, pid)
      return prev.map(beat => {
        if (beat.id === a.id) return { ...beat, position: posB }
        if (beat.id === b.id) return { ...beat, position: posA }
        return beat
      })
    })
  }

  function moveToAct(id, newAct) {
    setBeats(prev => {
      const actBeats = prev.filter(b => b.act === newAct)
      const newPos = actBeats.length > 0 ? Math.max(...actBeats.map(b => b.position)) + 1 : 0
      api.updatePlotBeat(id, { act: newAct, position: newPos }, pid)
      return prev.map(b => b.id === id ? { ...b, act: newAct, position: newPos } : b)
    })
  }

  function scrollToBeat(id) {
    const el = beatRefs.current[id]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.classList.add('beat-highlight')
      setTimeout(() => el.classList.remove('beat-highlight'), 1500)
    }
  }

  const totalBeats = beats.length
  const act1Count  = beats.filter(b => b.act === 1).length
  const act3Count  = beats.filter(b => b.act === 3).length

  let structureHint = null
  if (totalBeats > 0) {
    if (act1Count / totalBeats > 0.4) {
      structureHint = 'Act I may be top-heavy — consider moving some beats to Act II'
    } else if (act3Count / totalBeats < 0.1) {
      structureHint = 'Act III looks light — resolution beats help the reader feel closure'
    } else {
      structureHint = 'Your story structure looks balanced 🌿'
    }
  }

  return (
    <div>
      {/* Overview strip */}
      {beats.length > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <div className="plot-overview-strip">
            {[1, 2, 3].flatMap(act =>
              beats
                .filter(b => b.act === act)
                .sort((a, b) => a.position - b.position)
                .map(beat => (
                  <PlotDot key={beat.id} beat={beat} onClick={scrollToBeat} />
                ))
            )}
          </div>
          {structureHint && (
            <p style={{ fontSize: '0.72rem', color: 'var(--stone)', marginTop: '0.25rem', fontStyle: 'italic' }}>
              {structureHint}
            </p>
          )}
        </div>
      )}

      {/* Three-act columns */}
      <div className="plot-columns">
        {ACTS.map(act => {
          const actBeats = beats
            .filter(b => b.act === act.num)
            .sort((a, b) => a.position - b.position)

          return (
            <div key={act.num} className="plot-col">
              <div className="plot-column-header" style={{ borderTopColor: ACT_COLORS[act.num] }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{act.label}</span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--stone)' }}>{actBeats.length}</span>
                </div>
                <div className="plot-column-hint">{act.hint}</div>
              </div>

              {actBeats.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '1.5rem 0.5rem', color: 'var(--stone)', fontSize: '0.82rem', fontStyle: 'italic' }}>
                  No beats yet
                </div>
              ) : (
                <div>
                  {actBeats.map(beat => (
                    <div key={beat.id} ref={el => beatRefs.current[beat.id] = el}>
                      <BeatCard
                        beat={beat}
                        beats={beats}
                        onEdit={openEdit}
                        onMoveUp={() => moveWithinAct(beat.id, -1)}
                        onMoveDown={() => moveWithinAct(beat.id, 1)}
                        onMoveToAct={moveToAct}
                      />
                    </div>
                  ))}
                </div>
              )}

              <button
                className="b-col-add-btn"
                onClick={() => openAdd(act.num)}
                style={{ marginTop: 6 }}
              >
                + Add beat
              </button>
            </div>
          )
        })}
      </div>

      {panelOpen && (
        <BeatPanel
          key={editBeat?.id || 'new'}
          beat={editBeat}
          prefilledAct={prefilledAct}
          pid={pid}
          chapters={chapters}
          onClose={() => setPanelOpen(false)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
}
